import { useState, useEffect } from 'react';

const EVER_CONTROLLED_KEY = 'msales_sw_ever_controlled';
const CHECK_INTERVAL_MS = 30000;
const CONTROLLER_LOSS_GRACE_PERIOD_MS = 5000;

let reportedBrokenInSession = false;

const hasEverBeenControlled = () => {
  try {
    return localStorage.getItem(EVER_CONTROLLED_KEY) === '1';
  } catch {
    return false;
  }
};

const markControlled = () => {
  try {
    localStorage.setItem(EVER_CONTROLLED_KEY, '1');
  } catch {}
};

export const useServiceWorkerHealth = () => {
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;

    let cancelled = false;
    let controllerLostAt = null;

    if (navigator.serviceWorker.controller) markControlled();

    const evaluate = () => {
      if (cancelled) return;

      if (navigator.serviceWorker.controller) {
        markControlled();
        setBroken(false);
        reportedBrokenInSession = false;
        controllerLostAt = null;
        return;
      }

      if (!hasEverBeenControlled()) return;

      const now = Date.now();
      if (controllerLostAt === null) {
        controllerLostAt = now;
        return;
      }
      if (now - controllerLostAt < CONTROLLER_LOSS_GRACE_PERIOD_MS) return;

      if (!reportedBrokenInSession) {
        reportedBrokenInSession = true;
        console.warn(
          `[ServiceWorker] Health degraded — fix banner shown to user (controller missing ${now - controllerLostAt}ms)`,
        );
      }
      setBroken(true);
    };

    const onControllerChange = () => {
      if (cancelled) return;
      if (navigator.serviceWorker.controller) {
        markControlled();
        setBroken(false);
        reportedBrokenInSession = false;
        controllerLostAt = null;
      } else if (controllerLostAt === null) {
        controllerLostAt = Date.now();
      }
    };

    evaluate();
    const interval = setInterval(evaluate, CHECK_INTERVAL_MS);
    navigator.serviceWorker.addEventListener(
      'controllerchange',
      onControllerChange,
    );

    return () => {
      cancelled = true;
      clearInterval(interval);
      navigator.serviceWorker.removeEventListener(
        'controllerchange',
        onControllerChange,
      );
    };
  }, []);

  return broken;
};
